import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  GraduationCap, Building2, Plus, Search, MoreHorizontal, 
  ArrowLeft, Loader2, AlertCircle, Pencil, Trash2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

import { API_URL } from '../utils/api';

export function OrganizationsPage() {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', type: 'district', address: '', phone: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/organizations`, { withCredentials: true });
      setOrganizations(response.data);
    } catch (err) {
      setError('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/organizations`, formData, { withCredentials: true });
      setCreateOpen(false);
      setFormData({ name: '', type: 'district', address: '', phone: '', email: '' });
      fetchOrganizations();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create organization');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (orgId) => {
    if (!window.confirm('Are you sure you want to delete this organization?')) return;
    try {
      await axios.delete(`${API_URL}/api/organizations/${orgId}`, { withCredentials: true });
      fetchOrganizations();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete organization');
    }
  };

  const filteredOrgs = organizations.filter(org =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isAdmin = user?.role === 'admin';

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white border-b border-[#E5E5E5]">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="p-2 hover:bg-[#F3F4F1] rounded-lg" data-testid="back-btn">
                <ArrowLeft className="h-5 w-5 text-[#595959]" />
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <GraduationCap className="h-7 w-7 text-[#2C4C3B]" />
                <span className="font-outfit text-xl font-semibold text-[#1C1C1C]">FrameEd</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-[#1C1C1C] mb-1">
              Organizations
            </h1>
            <p className="text-[#595959]">Manage districts and organizations in your ecosystem</p>
          </div>
          {isAdmin && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white rounded-xl" data-testid="create-org-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Organization
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-outfit">Create Organization</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Organization name"
                      className="rounded-xl"
                      required
                      data-testid="org-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={formData.type} onValueChange={(v) => setFormData({...formData, type: v})}>
                      <SelectTrigger className="rounded-xl" data-testid="org-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="district">District</SelectItem>
                        <SelectItem value="school">School</SelectItem>
                        <SelectItem value="provider">Service Provider</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="contact@organization.edu"
                      className="rounded-xl"
                      data-testid="org-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      placeholder="123 Main St, City, State"
                      className="rounded-xl"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="rounded-xl">
                      Cancel
                    </Button>
                    <Button type="submit" disabled={submitting} className="bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl" data-testid="submit-org-btn">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Create
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 p-4 mb-6 bg-red-50 border border-red-200 rounded-xl text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#595959]" />
          <Input
            placeholder="Search organizations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-xl bg-white"
            data-testid="search-orgs-input"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#2C4C3B]" />
          </div>
        ) : filteredOrgs.length === 0 ? (
          <div className="text-center py-12 bg-white border border-[#E5E5E5] rounded-2xl">
            <Building2 className="h-12 w-12 mx-auto text-[#595959] mb-4" />
            <h3 className="font-outfit text-lg font-medium text-[#1C1C1C] mb-2">No organizations yet</h3>
            <p className="text-[#595959] mb-4">Get started by creating your first organization</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredOrgs.map((org) => (
              <div
                key={org.org_id}
                className="bg-white border border-[#E5E5E5] rounded-2xl p-6 hover:shadow-lg transition-shadow"
                data-testid={`org-card-${org.org_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#2C4C3B]/10 flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-[#2C4C3B]" />
                    </div>
                    <div>
                      <h3 className="font-outfit text-lg font-semibold text-[#1C1C1C]">{org.name}</h3>
                      <p className="text-sm text-[#595959] capitalize">{org.type}</p>
                      {org.email && <p className="text-sm text-[#595959] mt-1">{org.email}</p>}
                      {org.address && <p className="text-sm text-[#595959]">{org.address}</p>}
                    </div>
                  </div>
                  {isAdmin && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(org.org_id)} className="text-red-600">
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
